import * as React from 'react'

import { Box, Tag, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Tooltip from 'modules/core/components/Tooltip'
import UserAvatar from 'modules/core/components/UserAvatar'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import users from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import { DueDate } from 'modules/processes/components/MaterializedProcessesTable/NextTaskDueDate'
import { TaskDetail } from 'modules/tasks/components/TaskElement/useTaskDetail'
import { statusOption } from 'modules/tasks/lib/status'

interface Props {
  task: TaskDetail
  stepName?: string | null
  mandatory?: boolean
  assignees: React.ReactNode
  affectee: React.ReactNode
  dueOn: string | null | undefined
}

const Info = ({ task, mandatory, stepName, assignees, affectee, dueOn }: Props) => {
  const author = 'author' in task ? users.get(task.author?.id) : undefined
  const taskCreatedFromProject = task.identifier === 'project'

  const formatedDate = dueOn ? stringToDate(dueOn, 'backend_datetime') : null
  const completedAt =
    'completedAt' in task && task.completedAt
      ? dateToString(stringToDate(task.completedAt, 'backend_datetime'), 'ui')
      : null

  const completedBy =
    'completedBy' in task && task.completedBy ? users.get(task.completedBy?.id) : undefined
  const selectedStatusOption = task.status ? statusOption(task.status) : undefined

  return (
    <Box gap="s16">
      <Text.Normal weight="semibold" color="grey1000">
        {i18n.t('tasks.labels.details')}
      </Text.Normal>
      <Box
        gap="s32"
        padding="s32"
        border={{
          all: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
        borderRadius={{
          topLeft: 'abs008',
          topRight: 'abs008',
          bottomLeft: 'abs008',
          bottomRight: 'abs008',
        }}
      >
        <Box flexDirection="column" gap="s24">
          {selectedStatusOption && (
            <TaskDetailRow label={i18n.t('tasks.labels.status')}>
              <Tag showCircle type={selectedStatusOption?.type} inline>
                {selectedStatusOption?.title}
              </Tag>
            </TaskDetailRow>
          )}
          <TaskDetailRow label={i18n.t('tasks.labels.workflow_task_for')}>{affectee}</TaskDetailRow>

          <TaskDetailRow label={i18n.t('tasks.labels.due_date')}>
            <DueDate date={formatedDate} />
          </TaskDetailRow>

          {!taskCreatedFromProject && (
            <TaskDetailRow label={i18n.t('tasks.labels.mandatory_task')}>
              <Tag inline type={mandatory ? 'tangerine' : 'lime'}>
                {mandatory ? i18n.t('tasks.labels.yes') : i18n.t('tasks.labels.no')}
              </Tag>
            </TaskDetailRow>
          )}

          {!taskCreatedFromProject && (
            <TaskDetailRow label={i18n.t('tasks.labels.step')} info={stepName} />
          )}

          <TaskDetailRow label={i18n.t('tasks.labels.assignees')}>{assignees}</TaskDetailRow>

          {author && <AvatarDetailRow text={i18n.t('tasks.labels.created_by')} access={author} />}

          <TaskDetailRow info={completedAt} label={i18n.t('tasks.labels.completed_at')} />

          <AvatarDetailRow text={i18n.t('tasks.labels.completed_by')} access={completedBy} />
        </Box>
      </Box>
    </Box>
  )
}

export default observer(Info)

const AvatarDetailRow = ({ access, text }: { access?: User; text: string }) => {
  if (!access) return null

  return (
    <TaskDetailRow label={text}>
      <Tooltip.Alt
        attachment="top center"
        label={getFullDisplayName({
          firstName: access.attributes.first_name,
          lastName: access.attributes.last_name,
        })}
        textAlign="center"
      >
        <UserAvatar user={access} size="s32" />
      </Tooltip.Alt>
    </TaskDetailRow>
  )
}

interface TaskDetailRowProps
  extends React.PropsWithChildren<{ label: string; info?: string | React.ReactNode | null }> {}

const TaskDetailRow = ({ label, info, children }: TaskDetailRowProps) => {
  const renderInfo = !children && info

  if (!children && !info) return null

  return (
    <Box alignItems="center" flexDirection="row" justifyContent="spaceBetween">
      <Text.Normal size="200" weight="medium" color="grey800">
        {label}
      </Text.Normal>
      {children}
      {renderInfo && (
        <Text.Normal size="200" weight="medium">
          {info}
        </Text.Normal>
      )}
    </Box>
  )
}
